import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ShButtonComponent } from '../button/button.component';
import { ShTooltipDirective } from '../tooltip/tooltip.directive';
import {
  fitTransform,
  panBy,
  toCssTransform,
  wheelZoomFactor,
  ZOOM_STEP,
  zoomAt,
  ZoomTransform,
} from './zoom-pane.math';

/** Elements a drag must not start a pan on (the content marks its own with `data-no-pan`). */
const NO_PAN_SELECTOR =
  'button, a, input, select, textarea, label, [data-no-pan]';
/** Pointer travel (px) before a press becomes a pan (so a click stays a click). */
const PAN_THRESHOLD = 4;

/**
 * A pane that shows its projected content scaled and shifted, with a toolbar
 * (zoom out / in, fit, 100%), Ctrl+wheel zoom around the cursor and drag to
 * pan on the empty canvas.
 *
 *   <sh-zoom-pane height="70vh" [minScale]="0.2" [maxScale]="2">
 *     ...any content...
 *   </sh-zoom-pane>
 *
 * The content fits into the pane when it first renders and again when it
 * (or the pane) resizes, until the user zooms by hand; the Fit button (or
 * `fit()`) brings that automatic fit back. A press on a button, link, input
 * or an element with `data-no-pan` inside the content never starts a pan, and
 * a drag that panned does not turn into a click.
 *
 * Styling: `@seahorse/theme`, `_zoom-pane.scss`. The maths are pure
 * functions in `zoom-pane.math.ts`.
 */
@Component({
  selector: 'sh-zoom-pane',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, ShButtonComponent, ShTooltipDirective],
  host: { '[style.height]': 'height()' },
  template: `
    <div class="zoom-pane-toolbar" role="toolbar">
      <sh-button
        variant="text"
        size="sm"
        icon="fa-solid fa-minus"
        ariaLabel="shared.zoomPane.zoomOut"
        shTooltip="shared.zoomPane.zoomOut"
        tooltipPosition="bottom"
        [disabled]="atMin()"
        (click)="zoomOut()"
      ></sh-button>
      <span class="zoom-pane-scale">{{ percent() }}%</span>
      <sh-button
        variant="text"
        size="sm"
        icon="fa-solid fa-plus"
        ariaLabel="shared.zoomPane.zoomIn"
        shTooltip="shared.zoomPane.zoomIn"
        tooltipPosition="bottom"
        [disabled]="atMax()"
        (click)="zoomIn()"
      ></sh-button>
      <sh-button
        variant="text"
        size="sm"
        icon="fa-solid fa-expand"
        ariaLabel="shared.zoomPane.fit"
        shTooltip="shared.zoomPane.fit"
        tooltipPosition="bottom"
        (click)="fit()"
      ></sh-button>
      <sh-button
        variant="text"
        size="sm"
        icon="fa-solid fa-magnifying-glass"
        ariaLabel="shared.zoomPane.actualSize"
        shTooltip="shared.zoomPane.actualSize"
        tooltipPosition="bottom"
        (click)="resetZoom()"
      ></sh-button>
    </div>
    <div #viewport class="zoom-pane-viewport" [class.is-panning]="panning()">
      <div #content class="zoom-pane-content" [style.transform]="css()">
        <ng-content></ng-content>
      </div>
    </div>
  `,
})
export class ShZoomPaneComponent {
  readonly minScale = input(0.2);
  readonly maxScale = input(2);
  /** Any CSS height of the pane. */
  readonly height = input('480px');
  /** Space (px) kept around the content by Fit. */
  readonly fitPadding = input(16);
  /** Fit never scales the content above this (1 = never enlarges it). */
  readonly maxFitScale = input(1);

  private readonly _viewport =
    viewChild.required<ElementRef<HTMLElement>>('viewport');
  private readonly _content =
    viewChild.required<ElementRef<HTMLElement>>('content');
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _transform = signal<ZoomTransform>({
    scale: 1,
    x: 0,
    y: 0,
  });
  /** The current scale (1 = 100%). */
  readonly scale = computed(() => this._transform().scale);
  protected readonly css = computed(() => toCssTransform(this._transform()));
  protected readonly percent = computed(() => Math.round(this.scale() * 100));
  protected readonly atMin = computed(
    () => this.scale() <= this.minScale() + 1e-9
  );
  protected readonly atMax = computed(
    () => this.scale() >= this.maxScale() - 1e-9
  );
  protected readonly panning = signal(false);

  // True once the user zoomed by hand: resizes no longer re-fit.
  private _userZoomed = false;
  private _press: { id: number; x: number; y: number } | null = null;

  constructor() {
    afterNextRender(() => this.attach());
  }

  /** Fits the whole content into the pane and resumes automatic fitting. */
  fit(): void {
    this._userZoomed = false;
    this.applyFit();
  }

  zoomIn(): void {
    this.zoomAtCentre(ZOOM_STEP);
  }

  zoomOut(): void {
    this.zoomAtCentre(1 / ZOOM_STEP);
  }

  /** 100%, around the centre of the pane. */
  resetZoom(): void {
    this.zoomAtCentre(1 / this.scale());
  }

  private zoomAtCentre(factor: number): void {
    const viewport = this._viewport().nativeElement;
    this.zoomAtPoint(
      factor,
      viewport.clientWidth / 2,
      viewport.clientHeight / 2
    );
  }

  private zoomAtPoint(factor: number, cx: number, cy: number): void {
    this._userZoomed = true;
    this._transform.update((t) =>
      zoomAt(t, factor, cx, cy, this.minScale(), this.maxScale())
    );
  }

  private applyFit(): void {
    const viewport = this._viewport().nativeElement;
    const content = this._content().nativeElement;
    // offsetWidth/Height are layout sizes: the scale does not change them.
    const next = fitTransform(
      { width: viewport.clientWidth, height: viewport.clientHeight },
      { width: content.offsetWidth, height: content.offsetHeight },
      {
        minScale: this.minScale(),
        maxScale: this.maxScale(),
        padding: this.fitPadding(),
        maxFitScale: this.maxFitScale(),
      }
    );
    this._transform.set(next);
  }

  private attach(): void {
    const viewport = this._viewport().nativeElement;
    const content = this._content().nativeElement;

    this.applyFit();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => {
        if (!this._userZoomed) this.applyFit();
      });
      observer.observe(viewport);
      observer.observe(content);
      this._destroyRef.onDestroy(() => observer.disconnect());
    }

    // Not passive: Ctrl+wheel must not zoom the page itself.
    const onWheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      const rect = viewport.getBoundingClientRect();
      const delta = event.deltaMode === 1 ? event.deltaY * 16 : event.deltaY;
      this.zoomAtPoint(
        wheelZoomFactor(delta),
        event.clientX - rect.left,
        event.clientY - rect.top
      );
    };
    const onPointerDown = (event: PointerEvent) => {
      if (event.button !== 0) return;
      if ((event.target as Element).closest(NO_PAN_SELECTOR)) return;
      this._press = { id: event.pointerId, x: event.clientX, y: event.clientY };
    };
    const onPointerMove = (event: PointerEvent) => {
      const press = this._press;
      if (!press || event.pointerId !== press.id) return;
      if (event.buttons === 0) {
        // The button was released outside the pane: no pan in progress.
        this.endPress(viewport, false);
        return;
      }
      const dx = event.clientX - press.x;
      const dy = event.clientY - press.y;
      if (!this.panning()) {
        if (Math.hypot(dx, dy) < PAN_THRESHOLD) return;
        this.panning.set(true);
        viewport.setPointerCapture(event.pointerId);
      }
      this._press = { id: press.id, x: event.clientX, y: event.clientY };
      this._transform.update((t) => panBy(t, dx, dy));
    };
    const onPointerEnd = () => this.endPress(viewport, this.panning());

    viewport.addEventListener('wheel', onWheel, { passive: false });
    viewport.addEventListener('pointerdown', onPointerDown);
    viewport.addEventListener('pointermove', onPointerMove);
    viewport.addEventListener('pointerup', onPointerEnd);
    viewport.addEventListener('pointercancel', onPointerEnd);
    this._destroyRef.onDestroy(() => {
      viewport.removeEventListener('wheel', onWheel);
      viewport.removeEventListener('pointerdown', onPointerDown);
      viewport.removeEventListener('pointermove', onPointerMove);
      viewport.removeEventListener('pointerup', onPointerEnd);
      viewport.removeEventListener('pointercancel', onPointerEnd);
    });
  }

  private endPress(viewport: HTMLElement, wasPanning: boolean): void {
    this._press = null;
    if (!wasPanning) return;
    this.panning.set(false);
    // The click that follows a pan must not reach what is under the cursor.
    const swallow = (event: Event) => event.stopPropagation();
    viewport.addEventListener('click', swallow, { capture: true, once: true });
    setTimeout(() => viewport.removeEventListener('click', swallow, true));
  }
}
